/* Model routing — built to the Claude Design reference's "Model routing" screen (`Lumiq App.dc.html`).
 *
 * Which backend serves this tenant's AI features. `core.ai.serving` had a complete API, a generated
 * client and no importer, so routing was configurable only by curl and `LLM_SERVING` reported
 * MISSING on every deployment — which made five working AI features look broken. This is the screen
 * that closes that: one PUT, and the whole AI plane has something to answer with.
 *
 * **Three states, not two, and the reference makes that its thesis rather than a detail.** Its lede
 * says they "must never collapse into two", and its layout is a three-card legend in which the card
 * matching the tenant's actual state carries the live values. An unrouted tenant, a route somebody
 * switched off, and a live route are different facts: `core.ai.anomaly` reports the middle one as
 * `TEMPLATE_ONLY` *by configuration* where an unreachable endpoint is `UNAVAILABLE`, and collapsing
 * them once already meant an admin closing a maintenance window had every anomalous finding reported
 * as a platform outage.
 *
 * **The reference shows no form, and the form stays.** It is a static mockup of the states; removing
 * the only control that can configure a route would remove the feature. It is styled in the
 * reference's own card language instead.
 *
 * **Routing is per tenant and stored, never an environment variable.** A bank on the shared tier may
 * contractually require that its metadata never reaches a hosted endpoint while its neighbour has no
 * such term; a deployment-wide setting cannot express that.
 *
 * **The screen never asks for a key, and says so** — the reference's closing line, kept verbatim.
 * The credential is deployment configuration (`LUMIQ_LLM_API_KEY`) with an endpoint allow-list,
 * because `endpoint` here is *tenant*-controlled: attaching a shared key to whatever host a tenant
 * named would hand one customer the platform's credential.
 *
 * **The AI-plane boundaries card is gone from this screen, and nothing behind it moved.** It rendered
 * `/capabilities` — the rung, what is never sent, each purpose's validator — and was removed for
 * page length on request. The controls it described are enforced by types, validators, RLS and the
 * serving service, none of which read this page; `/capabilities` still serves the same statement.
 *
 * **The direct endpoint route is collapsed, not removed.** Tenants that stored one are still served
 * by it, so its form stays reachable; a configured provider replaces it once activated.
 *
 * **The air-gap refusal is shown early and enforced elsewhere.** A hosted backend is refused for an
 * air-gapped tenant by the domain, by the service with a 422, and by a CHECK constraint. All three
 * backends stay on offer and the band explains which is refused — an earlier build filtered the list
 * and left the page warning about a selection it no longer displayed.
 */

import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { SERVING_BACKENDS } from '@/shared/api/generated/ai-serving';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { CheckboxField, SelectField, TextField } from '@/shared/ui/Field';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { HelpLabel } from '@/shared/ui/InfoTip';

import { useConfigureRoute, useServingRoute } from '../api/serving';
import type { ServingBackend, ServingRouteView } from '../api/serving';
import type { RouteState } from '../model/presentation';
import { ConfigurationHistory, ProviderManagement } from './ProviderManagement';
import { backendKey, isRefusedPair, routeStateOf, stateKey } from '../model/presentation';

/** The reference's state card: a chip, then either the live values or what the state means.
 *
 * All three render always — that is the reference's layout and the point of its lede. The card
 * matching the tenant's actual state is the one carrying `Current:`, which is how the reference
 * distinguishes the live one without a separate "active" treatment.
 */
function StateCard({
  state,
  current,
  view,
}: {
  readonly state: RouteState;
  readonly current: boolean;
  readonly view: ServingRouteView | undefined;
}): ReactElement {
  const { t } = useTranslation();
  /* Only the tenant's own state is tinted, in its own tone, as the design draws it: the other two
     stay neutral, so which state this tenant is in is one glance rather than a reading of three
     coloured cards. The neutral state's own card stays neutral even when current — a blank is not a
     finding. */
  const rule = !current
    ? ''
    : state === 'live'
      ? ' lq-ai__card--ok'
      : state === 'switched_off'
        ? ' lq-ai__card--warn'
        : '';
  const tone = state === 'live' ? 'ok' : state === 'switched_off' ? 'warn' : 'unknown';
  return (
    <div className={`lq-ai__card${rule}`} aria-current={current ? 'true' : undefined}>
      <div className="lq-ai__chip-slot">
        <span
          className={`lq-ai__chip lq-ai__chip--${tone}`}
          title={t(`${stateKey(state)}Hint`)}
        >
          {/* Filled for a state that is a configuration somebody made, an outline for the blank —
              so "no route" reads as an absence in shape as well as in colour. */}
          <span
            className={`lq-ai__dot${state === 'unconfigured' ? '' : ' lq-ai__dot--filled'}`}
          />
          {t(stateKey(state))}
        </span>
      </div>
      <div className="lq-ai__card-body">
        {current && view?.configured === true ? (
          <>
            {t('serving.state.current')}{' '}
            {/* The reference prints `backend · host · model` in one mono run. The host rather than
                the full endpoint, matching what the audit chain stores — a path is not worth
                showing here and `origin_of` is the platform's own rule for the same field. */}
            <span className="lq-ai__mono">
              {[view.backend, hostOf(view.endpoint), view.model]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </>
        ) : (
          t(`${stateKey(state)}.body`)
        )}
      </div>
    </div>
  );
}

/** The endpoint's host, for the reference's one-line `backend · host · model` summary.
 *
 * Mirrors the platform's own `origin_of`: a stored endpoint is a base URL and the host is the part
 * that answers "where is this going". Falls back to the raw string rather than throwing, because a
 * value that reached here has already been through the domain's URL guard and a summary line is not
 * the place to discover otherwise.
 */
function hostOf(endpoint: string | undefined): string {
  if (endpoint === undefined || endpoint === '') return '';
  try {
    return new URL(endpoint).host;
  } catch {
    return endpoint;
  }
}

export function ModelRoutingScreen(): ReactElement {
  const { t } = useTranslation();
  const route = useServingRoute();
  const configure = useConfigureRoute();

  const [backend, setBackend] = useState<ServingBackend>('llama_cpp');
  const [endpoint, setEndpoint] = useState('');
  const [model, setModel] = useState('');
  const [airGapped, setAirGapped] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [directOpen, setDirectOpen] = useState(false);

  /* The form starts from the stored route, so an admin changing one field does not silently replace
     the other three with this component's defaults — a PUT replaces the whole route, so an unseeded
     form is a way to blank an endpoint by editing a model name. Keyed on the fetch rather than on
     mount because the route arrives after the first render. */
  useEffect(() => {
    if (!route.isSuccess || !route.data.configured) return;
    const stored = route.data;
    setBackend(stored.backend ?? 'llama_cpp');
    setEndpoint(stored.endpoint ?? '');
    setModel(stored.model ?? '');
    setAirGapped(stored.air_gapped ?? false);
    setEnabled(stored.enabled ?? true);
  }, [route.isSuccess, route.data]);

  const refused = isRefusedPair(backend, airGapped);
  const incomplete = endpoint.trim() === '' || model.trim() === '';
  const state = route.isSuccess ? routeStateOf(route.data) : undefined;
  const states: readonly RouteState[] = ['unconfigured', 'switched_off', 'live'];

  return (
    <div className="lq-ai">
      <div className="lq-ai__head">
        <div className="lq-ai__head-main">
          <HelpLabel subject={t('serving.title')} help={t('help.feature.modelRouting')}>
            <h1 className="lq-ai__title">{t('serving.title')}</h1>
          </HelpLabel>
          <p className="lq-ai__lede">{t('serving.subtitle')}</p>
        </div>
      </div>

      {route.isPending && <SkeletonRows columns={3} rows={3} />}

      {route.isError && (
        <div className="lq-ai__stack">
          <Alert title={t('serving.readFailed')} tone="error">
            {route.error.message}
          </Alert>
        </div>
      )}

      {/* The reference's three-card legend. All three always, because the lede's claim is that the
          states must never collapse into two — and the one matching this tenant carries the values. */}
      {route.isSuccess && (
        <div className="lq-ai__triptych">
          {states.map((candidate) => (
            <StateCard
              current={candidate === state}
              key={candidate}
              state={candidate}
              view={route.data}
            />
          ))}
        </div>
      )}

      {/* Active model and fallback, then provider configuration: one dropdown and the chosen
          provider's form or models. */}
      <ProviderManagement />

      {/* The legacy direct route, collapsed. Still served for the tenants that stored one, so it
          stays reachable; it is no longer the first thing on the page. */}
      <section className="lq-ai__card lq-llm__section lq-llm__collapsible">
        <h2 className="lq-llm__heading">
          <button
            aria-controls="lq-llm-direct"
            aria-expanded={directOpen}
            className="lq-llm__toggle"
            onClick={() => {
              setDirectOpen((current) => !current);
            }}
            type="button"
          >
            <span aria-hidden="true" className="lq-llm__caret">
              {directOpen ? '▾' : '▸'}
            </span>
            {t('serving.llm.direct.title')}
          </button>
        </h2>
        {directOpen && (
          <div className="lq-llm__section" id="lq-llm-direct">
            <p className="lq-ai__muted">{t('serving.llm.direct.explain')}</p>

            {/* The refusal band, shown for the pair the platform will refuse. */}
            {refused && (
              <div className="lq-ai__refusal">
                <strong className="lq-ai__refusal-tag">
                  {t('serving.refusedPair.tag')}
                </strong>
                <span className="lq-ai__refusal-body">
                  {t('serving.refusedPair.detail')}
                </span>
              </div>
            )}

            <div className="lq-llm__form">
              <SelectField
                label={t('serving.field.backend')}
                onChange={setBackend}
                options={SERVING_BACKENDS.map((option) => ({
                  value: option,
                  label: t(backendKey(option)),
                }))}
                value={backend}
              />
              <TextField
                label={t('serving.field.endpoint')}
                maxLength={2000}
                onChange={setEndpoint}
                placeholder="http://llama.lumiq.svc.cluster.local:8080"
                required
                value={endpoint}
              />
              <TextField
                label={t('serving.field.model')}
                maxLength={200}
                onChange={setModel}
                placeholder="llama-3.1-8b-instruct"
                required
                value={model}
              />
            </div>
            <CheckboxField
              checked={airGapped}
              hint={t('serving.field.airGappedHint')}
              label={t('serving.field.airGapped')}
              onChange={setAirGapped}
            />
            <CheckboxField
              checked={enabled}
              hint={t('serving.field.enabledHint')}
              label={t('serving.field.enabled')}
              onChange={setEnabled}
            />

            {/* The server's message verbatim: a 403 and a 422 are both answers to read. */}
            {configure.isError && (
              <Alert title={t('serving.configure.failed')} tone="error">
                {configure.error.message}
              </Alert>
            )}
            {configure.isSuccess && (
              <Alert title={t('serving.configure.stored')} tone="notice">
                {t('serving.configure.storedDetail')}
              </Alert>
            )}
            <div className="lq-llm__footer">
              <Button
                busy={configure.isPending}
                disabled={refused || incomplete}
                onClick={() => {
                  configure.mutate({
                    backend,
                    endpoint: endpoint.trim(),
                    model: model.trim(),
                    air_gapped: airGapped,
                    enabled,
                  });
                }}
                tone="primary"
              >
                {t('serving.configure.action')}
              </Button>
            </div>
            <p className="lq-ai__footnote">{t('serving.credentialNote')}</p>
          </div>
        )}
      </section>

      <ConfigurationHistory />
    </div>
  );
}
